// src/components/FeaturedWork.tsx
import { useState, useEffect } from 'react'
import { AnimatedCard } from '@components/ui/AnimatedCard'
import { BlogCard } from '@components/blog/BlogCard'
import { PublicationCard } from '@components/publications/PublicationCard'
import { getBlogPosts } from '@data/BlogPostFiles/Blogs_index'
import type { BlogPost } from '@data/types'
import { publications } from '@data/PublicationFiles/Publications_index'

type WorkType = 'all' | 'blogs' | 'publications'

const sortByDate = <T extends { date: string }>(items: T[]): T[] =>
  [...items].sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())

export const FeaturedWork = () => {
  const [activeType, setActiveType] = useState<WorkType>('all')
  const [blogPosts, setBlogPosts] = useState<BlogPost[]>([])

  useEffect(() => {
    getBlogPosts()
      .then((posts) => setBlogPosts(posts))
      .catch((err) => console.error('Error loading blogs:', err))
  }, [])

  const featuredBlogs = sortByDate(blogPosts).slice(0, 3)
  const featuredPubs = sortByDate(publications).slice(0, 3)

  // Merge and sort for the "All" tab
  const merged = [
    ...featuredBlogs.map((b) => ({ ...b, kind: 'blog' as const })),
    ...featuredPubs.map((p) => ({ ...p, kind: 'publication' as const })),
  ]
  const featuredAll = sortByDate(merged).slice(0, 3)

  const renderContent = () => {
    if (activeType === 'blogs') {
      return (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {featuredBlogs.map((post, idx) => (
            <AnimatedCard key={post.id} delay={idx * 0.1}>
              <BlogCard post={post} showSocial />
            </AnimatedCard>
          ))}
        </div>
      )
    }
    if (activeType === 'publications') {
      return (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {featuredPubs.map((pub, idx) => (
            <AnimatedCard key={pub.id} delay={idx * 0.1}>
              <PublicationCard publication={pub} showSocial />
            </AnimatedCard>
          ))}
        </div>
      )
    }
    // 'all'
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
        {featuredAll.map((item, idx) => (
          <AnimatedCard key={item.id} delay={idx * 0.1}>
            {item.kind === 'blog' ? (
              <BlogCard post={item as BlogPost} showSocial />
            ) : (
              <PublicationCard publication={item as typeof publications[0]} showSocial />
            )}
          </AnimatedCard>
        ))}
      </div>
    )
  }

  return (
    <section className="py-20 px-4">
      <div className="container mx-auto">
        <header className="text-center mb-12">
          <h2 className="text-3xl font-bold text-text mb-4">Recent Work</h2>
          <p className="text-text/60 text-lg max-w-2xl mx-auto">
            My most recent blog posts and publications.
          </p>
        </header>

        <div className="flex justify-center mb-12">
          <div className="inline-flex bg-primary/10 rounded-lg p-1">
            {(['all', 'blogs', 'publications'] as WorkType[]).map((type) => (
              <button
                key={type}
                onClick={() => setActiveType(type)}
                className={`px-4 py-2 rounded-lg transition-colors ${
                  activeType === type
                    ? 'bg-primary text-white'
                    : 'text-text hover:bg-primary hover:text-white'
                }`}
              >
                {type === 'all' ? 'All' : type === 'blogs' ? 'Blogs' : 'Publications'}
              </button>
            ))}
          </div>
        </div>

        {renderContent()}
      </div>
    </section>
  )
}
